import clsx from 'clsx';
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from '@/hooks/queries/useAuthQueries';
import { useInfiniteNotifications, useMarkAllNotificationsRead } from '@/hooks/queries/useNotificationQueries';
import { useAuthStore } from '@/stores/authStore';
import { getPoolsUrl } from '../../utils/navigation';
import NotificationDropdown from '../notifications/NotificationDropdown';
import AuthButtons from './AuthButtons';
import MobileMenu from './MobileMenu';
import MobileMenuButton from './MobileMenuButton';
import NavbarLogo from './NavbarLogo';
import NotificationButton from './NotificationButton';
import ProfileButton from './ProfileButton';
import ProfileMenu from './ProfileMenu';

export type NavbarProps = {
  className?: string;
};

export default function Navbar({ className = '' }: NavbarProps) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [profileMenuVisible, setProfileMenuVisible] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const logoutMutation = useLogout();
  const { mutate: markAllNotificationsRead } = useMarkAllNotificationsRead();
  const { data: notificationsData } = useInfiniteNotifications({ isAuthenticated });
  const unreadCount = notificationsData?.pages[0]?.pagination.total ?? 0;

  // Refs for positioning
  const bellRef = useRef<HTMLButtonElement>(null);
  const profileButtonRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const showAuthButtons = !(
    location.pathname.startsWith('/terms-of-service') ||
    location.pathname.startsWith('/privacy-policy') ||
    location.pathname.startsWith('/authorized-agents')
  );

  const isNotificationsOpen = showNotifications && isAuthenticated;

  const handleProfileMenuToggle = () => {
    setProfileMenuVisible(!profileMenuVisible);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuVisible(!mobileMenuVisible);
  };

  const handleNotificationToggle = () => {
    setShowNotifications(!showNotifications);
  };

  const handleNotificationClose = () => {
    setShowNotifications(false);
  };

  const handleMarkAllAsRead = () => {
    markAllNotificationsRead();
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      setProfileMenuVisible(false);
      setShowNotifications(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handlePoolsDashboardClick = async () => {
    try {
      if (user) {
        // If we know the user role, navigate directly
        const targetUrl = getPoolsUrl();
        navigate(targetUrl);
        return;
      }
    } catch (e) {
      console.error('Failed to fetch user role:', e);
    }
  };

  return (
    <header
      id="main-navbar"
      className={clsx('w-full px-4 sm:px-6 py-4 sm:py-6 fixed top-0 left-0 bg-white z-50', className)}
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        {/* Logo */}

        <NavbarLogo />

        {/* Right section: notification bell + mobile menu or profile/auth (single set of components) */}
        <div className="flex items-center gap-3 lg:gap-4 relative">
          {isAuthenticated ? (
            <>
              <div className="scale-90 lg:scale-100">
                <NotificationButton
                  ref={bellRef}
                  unreadCount={unreadCount}
                  onClick={handleNotificationToggle}
                  isOpen={isNotificationsOpen}
                />
              </div>
              <div className="flex items-center gap-4 relative">
                <div className="scale-90 lg:scale-100">
                  <ProfileButton
                    ref={profileButtonRef}
                    onClick={handleProfileMenuToggle}
                    isMenuOpen={profileMenuVisible}
                  />
                </div>
                <ProfileMenu
                  isOpen={profileMenuVisible}
                  onClose={() => setProfileMenuVisible(false)}
                  onPoolsDashboardClick={handlePoolsDashboardClick}
                  onLogout={handleLogout}
                  anchorRef={profileButtonRef}
                />
              </div>
            </>
          ) : (
            <>
              <div className="lg:hidden">
                <MobileMenuButton isOpen={mobileMenuVisible} onClick={handleMobileMenuToggle} />
              </div>
              <div className="hidden lg:flex items-center gap-4">{showAuthButtons && <AuthButtons />}</div>
            </>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      <MobileMenu isOpen={mobileMenuVisible} onClose={handleMobileMenuToggle} />

      {/* Notification Dropdown - Rendered separately to work on both desktop and mobile */}
      {isAuthenticated && (
        <NotificationDropdown
          isOpen={isNotificationsOpen}
          onClose={handleNotificationClose}
          anchorRef={bellRef}
          onMarkAllAsRead={handleMarkAllAsRead}
        />
      )}
    </header>
  );
}
